'use client';

import { MessageCircleIcon, XIcon } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';

import { playSfx } from '@/hooks/use-sound';

/**
 * Lightweight always-loaded launcher for the chat dock. The heavy panel
 * bundle (ai-sdk, react-markdown, motion) is only downloaded on first open,
 * prefetched on FAB hover/focus or when the browser goes idle on desktop.
 */

const HINT_STORAGE_KEY = 'chat-hint-seen';

const ChatPanel = dynamic(
  () =>
    import('@/features/chat/components/chat-dock').then((mod) => mod.ChatPanel),
  { ssr: false }
);

function prefetchPanel() {
  void import('@/features/chat/components/chat-dock');
}

export function ChatLauncher() {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const fabRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!window.localStorage.getItem(HINT_STORAGE_KEY)) setShowHint(true);
    // No idle prefetch: downloading the ~500KB panel bundle during the load
    // window hurts TTI on mobile. The FAB's hover/focus/press prefetch keeps
    // first-open latency low for anyone who is about to use it.
  }, []);

  const dismissHint = () => {
    if (!showHint) return;
    setShowHint(false);
    window.localStorage.setItem(HINT_STORAGE_KEY, '1');
  };

  const toggle = () => {
    if (!open) {
      setLoaded(true);
      dismissHint();
    }
    playSfx(open ? 'close' : 'open');
    setOpen(!open);
  };

  // Called only while the dock is open (panel close button / Escape).
  const close = useCallback(() => {
    playSfx('close');
    setOpen(false);
    fabRef.current?.focus();
  }, []);

  return (
    <div className="fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] z-50 print:hidden">
      {loaded && <ChatPanel open={open} onClose={close} />}

      {showHint && !open && (
        <span
          aria-hidden="true"
          className="absolute top-1/2 right-full mr-3 -translate-y-1/2 rounded-full border border-edge bg-background px-3 py-1.5 font-mono text-xs whitespace-nowrap text-muted-foreground shadow-lg shadow-black/5 dark:shadow-black/30"
        >
          Chat with Victor&rsquo;s AI
        </span>
      )}

      <button
        ref={fabRef}
        type="button"
        data-sfx-managed=""
        onClick={toggle}
        onPointerEnter={prefetchPanel}
        onFocus={prefetchPanel}
        aria-label="Chat with Victor's AI"
        aria-expanded={open}
        aria-haspopup="dialog"
        className="flex size-12 items-center justify-center rounded-full border border-edge bg-background shadow-lg shadow-black/5 transition-[background-color,scale] hover:scale-105 hover:bg-accent2 active:scale-95 dark:shadow-black/30"
      >
        {open ? (
          <XIcon className="size-5" />
        ) : (
          <MessageCircleIcon className="size-5" />
        )}
      </button>
    </div>
  );
}
